import CloseIcon from "@mui/icons-material/Close";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../../../utils";

interface ShareableLinkDialogProps {
  open: boolean;
  onClose: () => void;
  agent: any;
  companyId: string;
  getToken: () => Promise<string>;
  onSuccess?: (message: string) => void;
}

const ShareableLinkDialog: React.FC<ShareableLinkDialogProps> = ({
  open,
  onClose,
  agent,
  companyId,
  getToken,
  onSuccess,
}) => {
  const [shortCode, setShortCode] = useState<string | null>(null);
  const [generatingCode, setGeneratingCode] = useState(false);

  // Reset shortCode when agent changes
  useEffect(() => {
    if (agent?.shortCode) {
      setShortCode(agent.shortCode);
    } else {
      setShortCode(null);
    }
  }, [agent]);

  // Auto-generate short code when dialog opens if agent doesn't have one
  useEffect(() => {
    if (open && agent && !agent.shortCode && !shortCode && !generatingCode) {
      generateShortCode();
    }
  }, [open, agent]);

  const generateShortCode = async () => {
    if (!agent || generatingCode) return;

    setGeneratingCode(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/agents/${agent.id}/short-code`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShortCode(response.data.shortCode);
    } catch (error) {
      console.error("Failed to generate short code:", error);
    } finally {
      setGeneratingCode(false);
    }
  };

  const getShareableUrl = () => {
    if (!shortCode) return "";
    const baseUrl = window.location.origin;
    return `${baseUrl}/a/${shortCode}`;
  };

  const getEmbedCode = () => {
    const url = getShareableUrl();
    if (!url) return "";
    return `<iframe
  src="${url}"
  style="width: 100%; height: 100%; min-height: 500px; border: none;"
  allow="microphone"
  title="${agent?.name || "AI Agent"}"
></iframe>`;
  };

  const copyEmbedCode = () => {
    const code = getEmbedCode();
    if (!code) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code);
      onSuccess?.("Embed code copied to clipboard!");
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      onSuccess?.("Embed code copied to clipboard!");
    }
  };

  const copyShareableUrl = () => {
    const url = getShareableUrl();
    if (!url) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url);
      onSuccess?.("Link copied to clipboard!");
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      onSuccess?.("Link copied to clipboard!");
    }
  };

  const openShareableUrl = () => {
    const url = getShareableUrl();
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Shareable Agent Page
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {agent && (
          <Box>
            {/* Agent Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 2,
              }}
            >
              {agent.avatarImage ? (
                <Avatar src={agent.avatarImage} sx={{ width: 48, height: 48 }} />
              ) : (
                <Avatar sx={{ width: 48, height: 48 }}>
                  {agent.name?.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {agent.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {agent.channelSettings?.website?.enabled !== false
                    ? "Website channel enabled"
                    : "Website channel disabled"}
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Share this link directly with users to let them chat with your agent.
              No website or embed code required - perfect for emails, texts, or social media.
            </Typography>

            {/* Shareable Link */}
            {generatingCode ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Generating shareable link...
                </Typography>
              </Box>
            ) : shortCode ? (
              <TextField
                fullWidth
                value={getShareableUrl()}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon color="primary" />
                    </InputAdornment>
                  ),
                  sx: {
                    fontFamily: "monospace",
                    fontSize: "14px",
                  },
                }}
                variant="outlined"
              />
            ) : (
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={generateShortCode}
                fullWidth
              >
                Generate Shareable Link
              </Button>
            )}

            {/* Embed Code Section */}
            {shortCode && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CodeOutlined color="primary" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Embed Code
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Embed this agent page in your website using an iframe. The page
                  automatically grows to fill its container.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  value={getEmbedCode()}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontFamily: "monospace",
                      fontSize: "13px",
                    },
                  }}
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyOutlined />}
                  onClick={copyEmbedCode}
                  sx={{ mt: 1 }}
                  size="small"
                >
                  Copy Embed Code
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {shortCode && (
          <>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={openShareableUrl}
            >
              Open Page
            </Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyOutlined />}
              onClick={copyShareableUrl}
            >
              Copy Link
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareableLinkDialog;
